import { useEffect, useRef, useCallback, useState } from 'react';

/**
 * useBrainPulse — connects to the Brain Pulse WebSocket.
 * Returns an array of recent pulse events (last ~10).
 * Each event: { event, type, contact, source, ts }
 */
export function useBrainPulse(token) {
  const [pulses, setPulses] = useState([]);
  const [thoughts, setThoughts] = useState([]);
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);

  const connect = useCallback(() => {
    if (!token) return;

    const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
    // Convert http(s) to ws(s)
    const wsUrl = baseUrl.replace(/^http/, 'ws') + '/api/dashboard/brain-pulse?token=' + token;

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      // Send a ping every 30s to keep alive
      ws._pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send('ping');
      }, 30000);
    };

    ws.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.event === 'memory_created') {
          setPulses(prev => [...prev.slice(-9), { ...data, ts: Date.now() }]);
        } else if (data.event === 'thought_completed') {
          setThoughts(prev => [...prev.slice(-9), { ...data, ts: Date.now() }]);
        }
      } catch {
        // ignore non-JSON (pong etc.)
      }
    };

    ws.onclose = () => {
      clearInterval(ws._pingInterval);
      // Reconnect after 5s
      reconnectTimer.current = setTimeout(connect, 5000);
    };

    ws.onerror = () => {
      ws.close();
    };
  }, [token]);

  useEffect(() => {
    connect();
    return () => {
      clearTimeout(reconnectTimer.current);
      if (wsRef.current) {
        clearInterval(wsRef.current._pingInterval);
        wsRef.current.close();
      }
    };
  }, [connect]);

  return { pulses, thoughts };
}
